import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import DOMPurify from 'isomorphic-dompurify';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConsentGuard } from '@design-system/embed';
import { HtmlContentWrapperWithStyle } from './HtmlContentWrapperWithStyle';
import { extractConsentGuardProps } from './utils/extractConsentGuardProps';
import { replaceIframesWithSpans } from './utils/replaceIframesWithSpans';
const sanitizeHtml = (html, config) => {
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.nodeName === 'IMG' && !node.hasAttribute('alt')) {
            node.setAttribute('alt', '');
        }
    });
    const sanitized = DOMPurify.sanitize(html, Object.assign(Object.assign({ USE_PROFILES: { html: true }, ADD_ATTR: ['target', 'alt'], FORBID_TAGS: ['form'] }, config), { RETURN_DOM_FRAGMENT: false, RETURN_DOM: false }));
    DOMPurify.removeHook('afterSanitizeAttributes');
    return sanitized;
};
export const HtmlContent = ({ config, content, stylesConfig, }) => {
    const [HtmlDiv, stylesConfigIsCustomCss] = useMemo(() => {
        if (stylesConfig === 'web' || stylesConfig === 'email') {
            return [HtmlContentWrapperWithStyle, false];
        }
        return [null, Boolean(stylesConfig)];
    }, [stylesConfig]);
    const containerRef = useRef(null);
    const [portals, setPortals] = useState([]);
    // TODO: This part needs cookie consent implementation
    const displayThirdPartyConsent = false;
    // const displayThirdPartyConsent = useMemo(
    //   () =>
    //     // Do not use destructuring. In some cases BO.currentNetwork
    //     // may be undefined for a while.
    //     get(
    //       currentNetwork,
    //       'features.cookies.config.display_third_party_consent',
    //     ),
    //   [currentNetwork],
    // );
    const { dangerouslySetInnerHTML, replacements } = useMemo(() => {
        const sanitizedHtml = sanitizeHtml(content, config);
        if (!displayThirdPartyConsent) {
            return {
                dangerouslySetInnerHTML: { __html: sanitizedHtml },
                replacements: [],
            };
        }
        const { modifiedHtml, replacements: iframeReplacements } = replaceIframesWithSpans(sanitizedHtml);
        return {
            dangerouslySetInnerHTML: { __html: modifiedHtml },
            iframeReplacements,
        };
    }, [config, content, displayThirdPartyConsent]);
    // We use a combination of useEffect (which runs after the
    // render is committed to the screen) and a ref (to get a stable
    // reference to the container for imperative DOM manipulation) to ensure
    // we can reliably retrieve the portals targets even if they are rendered
    // within this component.
    useEffect(() => {
        // TS has a hard time understanding containerRef.current is not null
        // if we access it directly. So we need to assign it to a variable first.
        const container = containerRef.current;
        if (container) {
            const newPortals = replacements === null || replacements === void 0 ? void 0 : replacements.map(({ iframe, spanId }) => {
                const span = container.querySelector(`#${spanId}`);
                if (!span) {
                    return null;
                }
                const { aspectRatio, hostName, providerName } = extractConsentGuardProps(iframe);
                return {
                    element: (_jsx(ConsentGuard, { aspectRatio: aspectRatio, hostName: hostName, isActive: true, providerName: providerName, children: _jsx("div", { dangerouslySetInnerHTML: { __html: iframe.outerHTML } }) })),
                    target: span,
                    key: spanId,
                };
            }).filter(Boolean);
            if (newPortals) {
                setPortals(newPortals);
            }
        }
    }, [replacements]);
    return (_jsxs(_Fragment, { children: [HtmlDiv ? (_jsx(HtmlDiv, { dangerouslySetInnerHTML: dangerouslySetInnerHTML, ref: containerRef })) : (_jsx("div", { 
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML: dangerouslySetInnerHTML, ref: containerRef, style: stylesConfigIsCustomCss
                    ? stylesConfig
                    : undefined })), portals.map(({ element, key, target }) => createPortal(element, target, key))] }));
};
const withIframeConfig = {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'scrolling'],
};
export const HtmlContentWithIframe = ({ content, stylesConfig, }) => (_jsx(HtmlContent, { config: withIframeConfig, content: content, stylesConfig: stylesConfig }));
//# sourceMappingURL=index.js.map